import type { InspectionReportDocument, PhotoSlot } from '@jobos/shared';

import { setSlotPhoto } from '@/lib/inspectionDocument';

// Which slots a pick from the job photo library fills, given the slot the user
// opened the picker on. Pure so the fill rule can be unit-tested without the
// editor, the same way planSlotDrop is.
//
// A pick on an *empty* slot is read as "start filling here": that slot plus
// every empty slot after it in layout order, so choosing four photos on a
// four-up page lays them out in one go. A pick on a *filled* slot is read as a
// replace of exactly that slot — the empty slots after it are not the user's
// subject, and quietly filling them would be a surprise.
export function planPhotoFill(
  slotOrder: string[],
  slots: Partial<Record<string, PhotoSlot>>,
  startKey: string,
): string[] {
  const start = slotOrder.indexOf(startKey);
  if (start === -1) return [];

  // Absent means untouched, which is empty: a page only stores the slots that
  // have been edited.
  if (slots[startKey]?.fileId) return [startKey];

  return slotOrder.slice(start).filter((key) => !slots[key]?.fileId);
}

/**
 * Write the picked photos into the planned slots, pairing them in order.
 *
 * One document back rather than one write per photo, so the editor applies a
 * multi-photo fill as a single change: one autosave, and one step to undo.
 * Whichever list runs out first ends the fill — more photos than slots is the
 * cap doing its job, more slots than photos just leaves the rest empty.
 */
export function fillSlotsWithPhotos(
  doc: InspectionReportDocument,
  pageId: string,
  slotKeys: string[],
  fileIds: string[],
): InspectionReportDocument {
  return slotKeys
    .slice(0, fileIds.length)
    .reduce((next, slotKey, i) => setSlotPhoto(next, pageId, slotKey, fileIds[i]!), doc);
}
